import DOMPurify from 'dompurify';
import { ChangeEvent, memo, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { utils as XLSXUtils, writeFile as writeXLSXFile } from 'xlsx';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Info } from 'lucide-react';
import {
  getPricingSummary,
  calculatePricing,
  type PricingSummary,
  type PricingCalculationRowInput,
  type PricingCalculationResult,
} from '../services/pricingService';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import {
  selectMeasurementUnit,
  setMeasurementUnit,
  selectSelectedCurrency,
  setSelectedCurrency,
} from '../features/preferences/preferencesSlice';
import {
  selectCurrencyOptions,
  selectCurrencyStatus,
  selectCurrencyError,
  fetchSupportedCurrencies,
} from '@/features/reference/referenceSlice';
import { convertInchesToUnit, convertUnitToInches, unitPrecision } from '../utils/measurement';
import { MEASUREMENT_UNITS, translateMeasurementUnitLabel, type MeasurementUnit } from '../types/measurement';
import { analytics } from '@/services/analytics';
import { logging } from '@/services/logging';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { useToast } from '@/hooks/use-toast';
import { DEFAULT_CURRENCY_CODE } from '@/constants/currency';
import type { CurrencyCode } from '@/constants/currency';
const MAX_DISCOUNT = 50;
const DEFAULT_DISCOUNT = 0;
const PRICING_PAGE_MAX_ROLL_WIDTH_INCHES = 24;
const DISCOUNT_SUGGESTIONS = [0, 10, 20, 25, 30] as const;
const PRICING_REQUEST_DEBOUNCE_MS = 2000;
const DIMENSION_INPUT_DEBOUNCE_MS = 2000;

const DEFAULT_SIZE_ROWS: BaseRow[] = [
  { labelKey: 'pricing.presets.us_13x18_5x7', fallbackLabel: 'US (13x18 cm) - 5x7 in', widthInches: 5, heightInches: 7, discount: DEFAULT_DISCOUNT },
  { labelKey: 'pricing.presets.a5_15x21_6x8', fallbackLabel: 'A5 (15x21 cm) - 6x8 in', widthInches: 6, heightInches: 8, discount: DEFAULT_DISCOUNT },
  { labelKey: 'pricing.presets.us_20x25_8x10', fallbackLabel: 'US (20x25 cm) - 8x10 in', widthInches: 8, heightInches: 10, discount: DEFAULT_DISCOUNT },
  { labelKey: 'pricing.presets.a4_21x30_8x12', fallbackLabel: 'A4 (21x30 cm) - 8x12 in', widthInches: 8, heightInches: 12, discount: DEFAULT_DISCOUNT },
  { labelKey: 'pricing.presets.us_23x30_5_9x12', fallbackLabel: 'US (23x30.5 cm) - 9x12 in', widthInches: 9, heightInches: 12, discount: DEFAULT_DISCOUNT },
  { labelKey: 'pricing.presets.us_25x33_10x13', fallbackLabel: 'US (25x33 cm) - 10x13 in', widthInches: 10, heightInches: 13, discount: DEFAULT_DISCOUNT },
  { labelKey: 'pricing.presets.us_28x35_11x14', fallbackLabel: 'US (28x35 cm) - 11x14 in', widthInches: 11, heightInches: 14, discount: DEFAULT_DISCOUNT },
  { labelKey: 'pricing.presets.a3_30x42_11x16', fallbackLabel: 'A3 (30x42 cm) - 11x16 in', widthInches: 11, heightInches: 16, discount: DEFAULT_DISCOUNT },
  { labelKey: 'pricing.presets.us_30x45_12x18', fallbackLabel: 'US (30x45 cm) - 12x18 in', widthInches: 12, heightInches: 18, discount: DEFAULT_DISCOUNT },
  { labelKey: 'pricing.presets.us_40x51_16x20', fallbackLabel: 'US (40x51 cm) - 16x20 in', widthInches: 16, heightInches: 20, discount: DEFAULT_DISCOUNT },
  { labelKey: 'pricing.presets.a2_42x59_16x23', fallbackLabel: 'A2 (42x59 cm) - 16x23 in', widthInches: 16, heightInches: 23, discount: DEFAULT_DISCOUNT },
  { labelKey: 'pricing.presets.us_45x60_18x24', fallbackLabel: 'US (45x60 cm) - 18x24 in', widthInches: 18, heightInches: 24, discount: DEFAULT_DISCOUNT },
  { labelKey: 'pricing.presets.b2_50x70_19x27', fallbackLabel: 'B2 (50x70 cm) - 19x27 in', widthInches: 19, heightInches: 27, discount: DEFAULT_DISCOUNT },
  { labelKey: 'pricing.presets.us_50x71_20x28', fallbackLabel: 'US (50x71 cm) - 20x28 in', widthInches: 20, heightInches: 28, discount: DEFAULT_DISCOUNT },
  { labelKey: 'pricing.presets.us_50x76_20x30', fallbackLabel: 'US (50x76 cm) - 20x30 in', widthInches: 20, heightInches: 30, discount: DEFAULT_DISCOUNT },
  { labelKey: 'pricing.presets.a1_59x84_23x33', fallbackLabel: 'A1 (59x84 cm) - 23x33 in', widthInches: 23, heightInches: 33, discount: DEFAULT_DISCOUNT },
  { labelKey: 'pricing.presets.us_61x91_24x36', fallbackLabel: 'US (61x91 cm) - 24x36 in', widthInches: 24, heightInches: 36, discount: DEFAULT_DISCOUNT },
];

type BaseRow = {
  labelKey: string;
  fallbackLabel: string;
  widthInches: number | null;
  heightInches: number | null;
  discount: number;
};

type RowParam = {
  label: string;
  widthInput: string;
  heightInput: string;
};

type PaperColumnDescriptor = {
  id: string;
  name: string;
  subtitle: string;
};

interface PricingTableHeaderProps {
  widthLabel: string;
  heightLabel: string;
  discountLabel: ReactNode;
  paperColumns: PaperColumnDescriptor[];
}

const arePaperColumnsEqual = (prev: PaperColumnDescriptor[], next: PaperColumnDescriptor[]) => {
  if (prev.length !== next.length) {
    return false;
  }
  for (let index = 0; index < prev.length; index += 1) {
    const prevPaper = prev[index];
    const nextPaper = next[index];
    if (
      prevPaper.id !== nextPaper.id ||
      prevPaper.name !== nextPaper.name ||
      prevPaper.subtitle !== nextPaper.subtitle
    ) {
      return false;
    }
  }
  return true;
};

const PricingTableHeader = memo<PricingTableHeaderProps>(
  ({ widthLabel, heightLabel, discountLabel, paperColumns }) => {
    return (
      <thead className="bg-muted/50 text-muted-foreground">
        <tr className="text-left">
          <th className="px-4 py-3 font-semibold">{widthLabel}</th>
          <th className="px-4 py-3 font-semibold">{heightLabel}</th>
          <th className="px-4 py-3 font-semibold">{discountLabel}</th>
          {paperColumns.map((paper) => (
            <th key={paper.id} className="px-4 py-3 font-semibold">
              <a
                href={`#paper-${paper.id}`}
                className="inline-flex flex-col text-left text-foreground hover:underline focus-visible:underline focus-visible:outline-none"
              >
                <span>{paper.name}</span>
                <span className="text-xs font-normal opacity-80">{paper.subtitle}</span>
              </a>
            </th>
          ))}
        </tr>
      </thead>
    );
  },
  (prev, next) =>
    prev.widthLabel === next.widthLabel &&
    prev.heightLabel === next.heightLabel &&
    prev.discountLabel === next.discountLabel &&
    arePaperColumnsEqual(prev.paperColumns, next.paperColumns)
);

const PRICING_TABLE_TOP_ANCHOR = 'pricing-table-top';

const fitsPricingPageRollWidth = (widthInches: number | null, heightInches: number | null): boolean => {
  if (widthInches == null || heightInches == null) {
    return true;
  }

  return Math.min(widthInches, heightInches) <= PRICING_PAGE_MAX_ROLL_WIDTH_INCHES;
};

const trimTrailingZeros = (value: string): string => value.replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '$1');

const scrollToPaperDescription = (paperId: string) => {
  if (typeof document === 'undefined') {
    return;
  }

  const element = document.getElementById(`paper-${paperId}`);
  if (!element) {
    return;
  }

  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const PricingPage = () => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const dispatch = useAppDispatch();
  const measurementUnit = useAppSelector(selectMeasurementUnit);
  const currencyOptions = useAppSelector(selectCurrencyOptions);
  const selectedCurrency = useAppSelector(selectSelectedCurrency);
  const currencyStatus = useAppSelector(selectCurrencyStatus);
  const currencyError = useAppSelector(selectCurrencyError);
  const precision = unitPrecision(measurementUnit);
  const unitLabel = useMemo(() => translateMeasurementUnitLabel(t, measurementUnit), [measurementUnit, t]);
  const [pricing, setPricing] = useState<PricingSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [baseRows, setBaseRows] = useState<BaseRow[]>(() => DEFAULT_SIZE_ROWS.map((row) => ({ ...row })));
  const [rowParams, setRowParams] = useState<RowParam[]>([]);
  const [pricingResult, setPricingResult] = useState<PricingCalculationResult | null>(null);
  const [pricingError, setPricingError] = useState<string | null>(null);
  const baseRowsRef = useRef(baseRows);
  const dimensionCommitTimeoutsRef = useRef<Map<string, number>>(new Map());
  const lastRollWidthToastKeyRef = useRef<string | null>(null);
  const getRowLabel = useCallback(
    (row: BaseRow) => t(row.labelKey, row.fallbackLabel),
    [t],
  );

  const formatDimensionInUnit = useCallback(
    (valueInches: number | null): string => {
      if (typeof valueInches !== 'number' || !Number.isFinite(valueInches) || valueInches <= 0) {
        return '';
      }
      const converted = convertInchesToUnit(valueInches, measurementUnit);
      if (!Number.isFinite(converted)) {
        return '';
      }
      return converted.toFixed(precision);
    },
    [measurementUnit, precision]
  );

  const formatRollWidthLimit = useCallback(() => {
    const converted = convertInchesToUnit(PRICING_PAGE_MAX_ROLL_WIDTH_INCHES, measurementUnit);
    return `${trimTrailingZeros(converted.toFixed(precision))} ${unitLabel}`;
  }, [measurementUnit, precision, unitLabel]);

  const notifyUnsupportedRollWidth = useCallback(
    (widthInches: number | null, heightInches: number | null, rowIndex: number) => {
      if (fitsPricingPageRollWidth(widthInches, heightInches)) {
        lastRollWidthToastKeyRef.current = null;
        return;
      }

      const toastKey = `${rowIndex}:${widthInches?.toFixed(4) ?? 'empty'}:${heightInches?.toFixed(4) ?? 'empty'}:${measurementUnit}`;
      if (lastRollWidthToastKeyRef.current === toastKey) {
        return;
      }

      lastRollWidthToastKeyRef.current = toastKey;
      toast({
        title: t('pricing.validation.roll_width_title', 'This print size is not available'),
        description: t(
          'pricing.validation.roll_width_description',
          'For online pricing, the smaller side must be {{limit}} or less. Please enter a smaller size.',
          { limit: formatRollWidthLimit() },
        ),
        variant: 'destructive',
      });
    },
    [formatRollWidthLimit, measurementUnit, t, toast],
  );

  useEffect(() => {
    let isMounted = true;

    const loadPricing = async () => {
      setLoading(true);
      setError(null);
      try {
        const currency = selectedCurrency ?? DEFAULT_CURRENCY_CODE;
        const data = await getPricingSummary(currency);
        if (!isMounted) return;
        setPricing(data);
      } catch (err) {
        if (!isMounted) return;
        setError(t('pricing.error_loading', 'Failed to load pricing. Please try again later.'));
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    };

    loadPricing();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCurrency]);

  useEffect(() => {
    if (currencyStatus === 'idle') {
      dispatch(fetchSupportedCurrencies());
    }
  }, [currencyStatus, dispatch]);

  useEffect(() => {
    setRowParams(
      baseRows.map((row) => ({
        label: getRowLabel(row),
        widthInput: formatDimensionInUnit(row.widthInches),
        heightInput: formatDimensionInUnit(row.heightInches),
      }))
    );
  }, [baseRows, formatDimensionInUnit, getRowLabel]);

  useEffect(() => {
    baseRowsRef.current = baseRows;
  }, [baseRows]);

  useEffect(
    () => () => {
      dimensionCommitTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
      dimensionCommitTimeoutsRef.current.clear();
    },
    []
  );

  useEffect(() => {
    dimensionCommitTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
    dimensionCommitTimeoutsRef.current.clear();
  }, [measurementUnit]);

  useEffect(() => {
    if (baseRows.length === 0 || (pricing?.paperTypes?.length ?? 0) === 0) {
      setPricingResult(null);
      return;
    }

    const controller = new AbortController();
    const rowsPayload: PricingCalculationRowInput[] = baseRows.map((row) => ({
      label: getRowLabel(row),
      widthInches: fitsPricingPageRollWidth(row.widthInches, row.heightInches) ? row.widthInches : null,
      heightInches: fitsPricingPageRollWidth(row.widthInches, row.heightInches) ? row.heightInches : null,
      discountPercentage: row.discount,
    }));

    const timeoutId = window.setTimeout(async () => {
      setPricingError(null);
      try {
        const result = await calculatePricing(rowsPayload, {
          signal: controller.signal,
          currency: selectedCurrency ?? DEFAULT_CURRENCY_CODE,
        });
        setPricingResult(result);
      } catch (err) {
        if (controller.signal.aborted) {
          return;
        }
        logging.error('Failed to calculate pricing rows', { error: err });
        setPricingError(t('pricing.error_calculation', 'Unable to calculate prices. Please try again.'));
      } finally {
        // no-op: calculation status is represented by result/error updates
      }
    }, PRICING_REQUEST_DEBOUNCE_MS);

    return () => {
      controller.abort();
      window.clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseRows, getRowLabel, pricing?.paperTypes, selectedCurrency]);
  const fallbackCurrencyCode = selectedCurrency ?? DEFAULT_CURRENCY_CODE;
  const activeCurrencyCode = pricing?.currency ?? fallbackCurrencyCode;

  const activeCurrencyMeta = useMemo(
    () => currencyOptions.find((option) => option.code === activeCurrencyCode),
    [currencyOptions, activeCurrencyCode],
  );

  const activeCurrencySymbol = activeCurrencyMeta?.symbol ?? activeCurrencyCode;

  const currencyFormatter = useMemo(() => {
    try {
      return new Intl.NumberFormat(i18n.language || undefined, {
        style: 'currency',
        currency: activeCurrencyCode,
      });
    } catch {
      return null;
    }
  }, [i18n.language, activeCurrencyCode]);

  const formatPrice = useCallback(
    (price: number | null | undefined) => {
      if (price == null) {
        return '—';
      }

      if (currencyFormatter) {
        return currencyFormatter.format(price);
      }

      return `${activeCurrencySymbol} ${price.toFixed(2)}`;
    },
    [currencyFormatter, activeCurrencySymbol],
  );

  const paperTypes = (pricing?.paperTypes ?? []) as PricingSummary['paperTypes'];

  const handleMeasurementUnitChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const nextUnit = event.target.value as MeasurementUnit;
      dispatch(setMeasurementUnit(nextUnit));
      analytics.track('select: ui.pricing_unit_change', { unit: nextUnit });
    },
    [dispatch]
  );

  const handleResetTable = useCallback(() => {
    setBaseRows(DEFAULT_SIZE_ROWS.map((row) => ({ ...row })));
    analytics.track('button: ui.pricing_reset_table');
  }, []);

  const handleDiscountChange = useCallback((index: number, value: string) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
      return;
    }

    setBaseRows((previous) =>
      previous.map((row, rowIndex) => {
        if (rowIndex !== index) {
          return row;
        }

        const clamped = Math.min(Math.max(numeric, 0), MAX_DISCOUNT);
        return {
          ...row,
          discount: clamped,
        };
      })
    );
    analytics.track('input: ui.pricing_discount_change', { rowIndex: index, discount: numeric });
  }, []);

  const commitDimensionValue = useCallback(
    (index: number, field: 'width' | 'height', rawValue: string) => {
      const inchesKey = field === 'width' ? 'widthInches' : 'heightInches';
      const inputKey = field === 'width' ? 'widthInput' : 'heightInput';
      const trimmedValue = rawValue.trim().replace(',', '.');

      if (trimmedValue === '') {
        setBaseRows((previous) =>
          previous.map((row, rowIndex) =>
            rowIndex === index
              ? row[inchesKey] == null
                ? row
                : ({
                    ...row,
                    [inchesKey]: null,
                  } as BaseRow)
              : row
          )
        );
        return;
      }

      const numeric = Number(trimmedValue);
      if (!Number.isFinite(numeric) || numeric <= 0) {
        const previousInches = baseRowsRef.current[index]?.[inchesKey] ?? null;
        const fallbackValue = formatDimensionInUnit(previousInches);
        setRowParams((previous) =>
          previous.map((row, rowIndex) =>
            rowIndex === index
              ? row[inputKey] === fallbackValue
                ? row
                : ({
                    ...row,
                    [inputKey]: fallbackValue,
                  } as RowParam)
              : row
          )
        );
        return;
      }

      const inches = convertUnitToInches(numeric, measurementUnit);
      const currentRow = baseRowsRef.current[index];
      const nextWidthInches = field === 'width' ? inches : currentRow?.widthInches ?? null;
      const nextHeightInches = field === 'height' ? inches : currentRow?.heightInches ?? null;
      notifyUnsupportedRollWidth(nextWidthInches, nextHeightInches, index);

      setBaseRows((previous) =>
        previous.map((row, rowIndex) =>
          rowIndex === index
            ? row[inchesKey] === inches
              ? row
              : ({
                  ...row,
                  [inchesKey]: inches,
                } as BaseRow)
            : row
        )
      );

      const formattedValue = formatDimensionInUnit(inches);
      setRowParams((previous) =>
        previous.map((row, rowIndex) =>
          rowIndex === index
            ? row[inputKey] === formattedValue
              ? row
              : ({
                  ...row,
                  [inputKey]: formattedValue,
                } as RowParam)
            : row
        )
      );
    },
    [formatDimensionInUnit, measurementUnit, notifyUnsupportedRollWidth]
  );

  const handleDimensionChange = useCallback(
    (index: number, field: 'width' | 'height', value: string) => {
      const inputKey = field === 'width' ? 'widthInput' : 'heightInput';
      const timerKey = `${index}:${field}`;
      const existingTimeoutId = dimensionCommitTimeoutsRef.current.get(timerKey);
      if (typeof existingTimeoutId === 'number') {
        window.clearTimeout(existingTimeoutId);
      }

      setRowParams((previous) =>
        previous.map((row, rowIndex) => {
          if (rowIndex !== index) {
            return row;
          }

          return {
            ...row,
            [inputKey]: value,
          } as RowParam;
        })
      );

      const timeoutId = window.setTimeout(() => {
        commitDimensionValue(index, field, value);
        dimensionCommitTimeoutsRef.current.delete(timerKey);
      }, DIMENSION_INPUT_DEBOUNCE_MS);
      dimensionCommitTimeoutsRef.current.set(timerKey, timeoutId);

      analytics.track('input: ui.pricing_dimension_change', {
        rowIndex: index,
        field,
        value,
        unit: measurementUnit,
      });
    },
    [commitDimensionValue, measurementUnit]
  );

  const handleDimensionBlur = useCallback(
    (index: number, field: 'width' | 'height', value: string) => {
      const timerKey = `${index}:${field}`;
      const existingTimeoutId = dimensionCommitTimeoutsRef.current.get(timerKey);
      if (typeof existingTimeoutId === 'number') {
        window.clearTimeout(existingTimeoutId);
        dimensionCommitTimeoutsRef.current.delete(timerKey);
      }
      commitDimensionValue(index, field, value);
    },
    [commitDimensionValue]
  );

  const currencySelectOptions = useMemo(
    () =>
      currencyOptions.map((option) => ({
        value: option.code,
        label: `${option.code} · ${option.label}`,
        emoji: option.emoji,
      })),
    [currencyOptions],
  );

  const handleCurrencyChange = useCallback(
    (code: string) => {
      const match = currencyOptions.find((option) => option.code === code);
      if (!match) {
        return;
      }
      setPricing(null);
      setPricingResult(null);
      dispatch(setSelectedCurrency(match.code as CurrencyCode));
      analytics.track('select: ui.pricing_currency_change', { currency: match.code });
    },
    [currencyOptions, dispatch],
  );

  const sortedPaperTypes = useMemo(() => {
    if (paperTypes.length <= 1) {
      return [...paperTypes];
    }

    if (!pricingResult) {
      return [...paperTypes];
    }

    const referenceIndex = baseRows.findIndex(
      (row) =>
        row.widthInches != null &&
        row.heightInches != null &&
        row.widthInches > 0 &&
        row.heightInches > 0
    );

    if (referenceIndex === -1) {
      return [...paperTypes];
    }

    return [...paperTypes].sort((paperA, paperB) => {
      const priceA = pricingResult.paperTypePrices[paperA.id]?.[referenceIndex] ?? null;
      const priceB = pricingResult.paperTypePrices[paperB.id]?.[referenceIndex] ?? null;

      if (priceA == null && priceB == null) {
        return 0;
      }
      if (priceA == null) {
        return 1;
      }
      if (priceB == null) {
        return -1;
      }
      return priceA - priceB;
    });
  }, [paperTypes, baseRows, pricingResult]);

  const widthHeaderLabel = useMemo(
    () => t('pricing.table.width', 'Width ({{unit}})', { unit: unitLabel }),
    [t, unitLabel]
  );
  const heightHeaderLabel = useMemo(
    () => t('pricing.table.height', 'Height ({{unit}})', { unit: unitLabel }),
    [t, unitLabel]
  );
  const discountHeaderLabel = useMemo(() => t('pricing.table.discount', 'Discount (%)'), [t]);
  const discountHeaderContent = useMemo(
    () => (
      <span className="inline-flex items-center gap-2">
        <span>{discountHeaderLabel}</span>
        <Link
          to="/discounts"
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-tan/70 text-brand-brown shadow-sm transition hover:bg-brand-tan hover:text-brand-brown"
          aria-label={t('pricing.table.discount_info', 'Learn about discounts')}
          title={t('pricing.table.discount_info', 'Learn about discounts')}
        >
          <Info className="h-4 w-4 leading-none" aria-hidden />
        </Link>
      </span>
    ),
    [discountHeaderLabel, t]
  );
  const paperColumnDescriptors = useMemo<PaperColumnDescriptor[]>(
    () =>
      sortedPaperTypes.map((paper) => ({
        id: paper.id,
        name: paper.name,
        subtitle: `${paper.brand} · ${paper.weightGsm}gsm`,
      })),
    [sortedPaperTypes]
  );

  const mobileDimensionSummaryLabel = useCallback(
    (row: RowParam) => {
      if (row.widthInput && row.heightInput) {
        return `${row.widthInput} × ${row.heightInput} ${unitLabel}`;
      }

      return t('pricing.mobile.enter_size', 'Enter print size');
    },
    [t, unitLabel],
  );

  const handleExportToExcel = useCallback(() => {
    if (!pricingResult || rowParams.length === 0 || sortedPaperTypes.length === 0) {
      window.alert(t('pricing.export.unavailable', 'Pricing data is still loading. Please try again.'));
      return;
    }

    const headerRow = [
      t('pricing.export.size_label', 'Size'),
      widthHeaderLabel,
      heightHeaderLabel,
      discountHeaderLabel,
      ...sortedPaperTypes.map((paper) => paper.name),
    ];

    const dataRows = rowParams.map((row, index) => {
      const baseRow = baseRows[index];
      const widthValue = row.widthInput || '';
      const heightValue = row.heightInput || '';
      const discountValue = `${baseRow?.discount ?? DEFAULT_DISCOUNT}%`;
      const priceCells = sortedPaperTypes.map((paper) =>
        formatPrice(pricingResult.paperTypePrices[paper.id]?.[index] ?? null)
      );

      return [row.label, widthValue, heightValue, discountValue, ...priceCells];
    });

    const worksheet = XLSXUtils.aoa_to_sheet([headerRow, ...dataRows]);
    const workbook = XLSXUtils.book_new();
    XLSXUtils.book_append_sheet(
      workbook,
      worksheet,
      t('pricing.export.sheet_name', 'Pricing Table')
    );

    const timestamp = new Date().toISOString().split('T')[0];
    writeXLSXFile(workbook, `pricing_${timestamp}.xlsx`);
    analytics.track('button: ui.pricing_export_excel');
  }, [
    baseRows,
    discountHeaderLabel,
    formatPrice,
    pricingResult,
    rowParams,
    sortedPaperTypes,
    t,
    widthHeaderLabel,
    heightHeaderLabel,
  ]);

  return (
    <div className="flex flex-col items-center min-h-screen bg-brand-background text-brand-brown p-4 pt-8 dark:bg-gray-900 dark:text-gray-300">
      <div className="w-full max-w-5xl space-y-8">
        <header id={PRICING_TABLE_TOP_ANCHOR}>
          <h1 className="text-3xl sm:text-4xl font-bold text-brand-green dark:text-gray-100">
            {t('pricing.title', 'Pricing')}
          </h1>
          <p className="mt-4 text-lg">
            {t(
              'pricing.description',
              'Our art printing service offers premium quality prints on professional-grade papers. Choose the size and paper type that best suits your artwork.'
            )}
          </p>
        </header>

        {loading && (
          <div className="rounded-lg border border-brand-tan/30 dark:border-amber-700/30 bg-white/60 dark:bg-gray-800/60 p-4 shadow">
            {t('pricing.loading', 'Loading pricing...')}
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 p-4 text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {!loading && !error && (paperTypes.length === 0 || rowParams.length === 0) && (
          <div className="rounded-lg border border-brand-tan/30 dark:border-amber-700/30 bg-white/60 dark:bg-gray-800/60 p-4 shadow">
            {t('pricing.no_data', 'Pricing information is temporarily unavailable.')}
          </div>
        )}

        <section className="space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <h2 className="pt-7 text-2xl font-semibold text-brand-brown dark:text-gray-100">
              {t('pricing.table_title', 'Example Print Sizes')}
            </h2>
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex min-w-[140px] flex-col gap-1">
                <span className="text-sm font-medium text-brand-brown dark:text-gray-200">
                  {t('pricing.controls.measurement_unit', 'Measurement unit')}
                </span>
                <select
                  id="measurementUnit"
                  value={measurementUnit}
                  onChange={handleMeasurementUnitChange}
                  className="rounded border border-brand-tan/40 bg-white px-3 py-2 text-sm dark:border-amber-700/40 dark:bg-gray-900"
                >
                  {MEASUREMENT_UNITS.map((unit) => (
                    <option key={unit} value={unit}>
                      {translateMeasurementUnitLabel(t, unit)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex min-w-[220px] flex-col gap-1">
                <span className="text-sm font-medium text-brand-brown dark:text-gray-200">
                  {t('pricing.controls.currency', 'Currency')}
                </span>
                <SearchableSelect
                  options={currencySelectOptions}
                  value={selectedCurrency}
                  onChange={handleCurrencyChange}
                  placeholder={t('pricing.controls.currency_placeholder', 'Select currency')}
                  searchPlaceholder={t('pricing.controls.currency_search', 'Search currency')}
                  noMatchesLabel={t('pricing.controls.currency_no_matches', 'No currencies found')}
                  disabled={currencyStatus === 'loading'}
                  className="flex items-center justify-between rounded border border-brand-tan/40 bg-white px-3 py-2 text-sm dark:border-amber-700/40 dark:bg-gray-900"
                  menuClassName="absolute z-50 mt-2 w-56 rounded border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900"
                  inputClassName="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-tan dark:border-gray-700 dark:bg-gray-800"
                  optionClassName="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-800"
                  selectedOptionClassName="w-full px-3 py-2 text-left text-sm font-semibold text-brand-brown dark:text-amber-300"
                />
                {currencyError && (
                  <span className="text-xs text-red-500">{currencyError}</span>
                )}
              </div>
              <button
                type="button"
                onClick={handleResetTable}
                className="w-full rounded border border-brand-green/40 bg-brand-green/10 px-3 py-2 text-sm font-medium text-brand-green hover:bg-brand-green/20 focus:outline-none focus:ring-2 focus:ring-brand-green/40 dark:border-emerald-300/40 dark:bg-emerald-400/10 dark:text-emerald-200 sm:w-auto"
              >
                {t('pricing.controls.reset_table', 'Reset table')}
              </button>
              <button
                type="button"
                onClick={handleExportToExcel}
                disabled={!pricingResult || sortedPaperTypes.length === 0}
                className="w-full rounded border border-brand-brown/30 bg-white px-3 py-2 text-sm font-medium text-brand-brown shadow-sm hover:bg-brand-tan/20 focus:outline-none focus:ring-2 focus:ring-brand-brown/30 disabled:cursor-not-allowed disabled:opacity-50 dark:border-amber-700/40 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800 sm:w-auto"
              >
                {t('pricing.controls.export_excel', 'Export to Excel')}
              </button>
            </div>
          </div>

          {pricingError && (
            <div className="rounded-md border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
              {pricingError}
            </div>
          )}

          <div className="space-y-4 md:hidden">
            <div className="rounded-2xl border border-brand-tan/25 bg-white/75 px-4 py-3 text-sm leading-relaxed text-brand-brown shadow-sm dark:border-amber-700/30 dark:bg-gray-800/70 dark:text-gray-300">
              {t(
                'pricing.mobile.full_list_hint',
                'To see the full price list, export it to Excel or open this page on desktop or in landscape mode.'
              )}
            </div>

            {rowParams[0] && baseRows[0] && (
              <article className="overflow-hidden rounded-[1.75rem] border border-brand-tan/25 bg-white/85 p-4 shadow-[0_18px_45px_rgba(83,55,33,0.08)] backdrop-blur dark:border-amber-700/30 dark:bg-gray-800/80">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.18em] text-brand-green dark:text-emerald-200">
                      {t('pricing.mobile.calculator_title', 'Price calculator')}
                    </p>
                    <p className="mt-1 text-xs uppercase tracking-[0.18em] text-brand-brown/70 dark:text-gray-400">
                      {mobileDimensionSummaryLabel(rowParams[0])}
                    </p>
                  </div>
                  <Link
                    to="/discounts"
                    className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-tan/20 text-brand-brown transition hover:bg-brand-tan/35 dark:bg-amber-800/30 dark:text-amber-200"
                    aria-label={t('pricing.table.discount_info', 'Learn about discounts')}
                  >
                    <Info className="h-4 w-4" aria-hidden />
                  </Link>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3">
                  <label className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-brand-brown/80 dark:text-gray-300">
                      {widthHeaderLabel}
                    </span>
                    <input
                      type="text"
                      value={rowParams[0].widthInput}
                      onChange={(event) => handleDimensionChange(0, 'width', event.target.value)}
                      onBlur={(event) => handleDimensionBlur(0, 'width', event.target.value)}
                      className="w-full rounded-xl border border-brand-tan/40 bg-white px-3 py-2 text-sm dark:border-amber-700/40 dark:bg-gray-900"
                      inputMode="decimal"
                      aria-label={t('pricing.table.width_input_label', 'Width for {label}', { label: rowParams[0].label })}
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-brand-brown/80 dark:text-gray-300">
                      {heightHeaderLabel}
                    </span>
                    <input
                      type="text"
                      value={rowParams[0].heightInput}
                      onChange={(event) => handleDimensionChange(0, 'height', event.target.value)}
                      onBlur={(event) => handleDimensionBlur(0, 'height', event.target.value)}
                      className="w-full rounded-xl border border-brand-tan/40 bg-white px-3 py-2 text-sm dark:border-amber-700/40 dark:bg-gray-900"
                      inputMode="decimal"
                      aria-label={t('pricing.table.height_input_label', 'Height for {label}', { label: rowParams[0].label })}
                    />
                  </label>
                </div>

                <div className="mt-3">
                  <label className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-brand-brown/80 dark:text-gray-300">
                      {discountHeaderLabel}
                    </span>
                    <select
                      value={(baseRows[0].discount ?? DEFAULT_DISCOUNT).toString()}
                      onChange={(event) => handleDiscountChange(0, event.target.value)}
                      className="w-full rounded-xl border border-brand-tan/40 bg-white px-3 py-2 text-sm dark:border-amber-700/40 dark:bg-gray-900"
                      aria-label={t('pricing.table.discount_input_label', 'Discount for row {index}', { index: 1 })}
                    >
                      {DISCOUNT_SUGGESTIONS.map((value) => (
                        <option key={value} value={value.toString()}>
                          {`${value}%`}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                {!fitsPricingPageRollWidth(baseRows[0].widthInches ?? null, baseRows[0].heightInches ?? null) && (
                  <div className="mt-4 rounded-2xl border border-red-300/50 bg-red-50/80 px-3 py-2 text-sm text-red-700 dark:border-red-800/60 dark:bg-red-950/20 dark:text-red-300">
                    {t('pricing.validation.roll_width_description', 'For online pricing, the smaller side must be {{limit}} or less. Please enter a smaller size.', { limit: formatRollWidthLimit() })}
                  </div>
                )}

                <div className="mt-4">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand-brown/60 dark:text-gray-400">
                    {t('pricing.mobile.paper_prices', 'Paper prices')}
                  </div>
                  <div className="space-y-2">
                    {sortedPaperTypes.map((paper: PricingSummary['paperTypes'][number]) => {
                      const price = pricingResult?.paperTypePrices[paper.id]?.[0] ?? null;
                      const isSupportedSize = fitsPricingPageRollWidth(
                        baseRows[0].widthInches ?? null,
                        baseRows[0].heightInches ?? null,
                      );
                      return (
                        <div
                          key={paper.id}
                          className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-2 rounded-2xl border border-brand-tan/20 bg-brand-background/65 px-3 py-2 dark:border-amber-700/20 dark:bg-gray-900/40"
                        >
                          <div className="min-w-0">
                            <button
                              type="button"
                              title={paper.name}
                              className="block w-full overflow-hidden text-left text-[13px] font-medium leading-[1.2] text-brand-brown hover:underline dark:text-gray-100"
                              style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', wordBreak: 'break-word' }}
                              onClick={() => scrollToPaperDescription(paper.id)}
                            >
                              {paper.name}
                            </button>
                            <div className="mt-1 truncate text-[11px] text-brand-brown/65 dark:text-gray-400">
                              {paper.brand} · {paper.weightGsm}gsm
                            </div>
                          </div>
                          <div className="shrink-0 pt-0.5 text-sm font-semibold text-brand-green dark:text-emerald-200">
                            {isSupportedSize ? formatPrice(price) : '—'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </article>
            )}
          </div>

          <div className="hidden overflow-x-auto md:block">
            <table className="w-full overflow-hidden rounded-lg border border-border/60 bg-background text-sm">
              <PricingTableHeader
                widthLabel={widthHeaderLabel}
                heightLabel={heightHeaderLabel}
                discountLabel={discountHeaderContent}
                paperColumns={paperColumnDescriptors}
              />
              <tbody>
                {rowParams.map((row, index) => {
                  const baseRow = baseRows[index];
                  const isSupportedSize = fitsPricingPageRollWidth(
                    baseRow?.widthInches ?? null,
                    baseRow?.heightInches ?? null,
                  );

                  return (
                    <tr
                      key={row.label}
                      className={`border-b border-border/60 transition-colors hover:bg-muted/40 ${
                        index % 2 === 1 ? 'bg-muted/20' : 'bg-transparent'
                      }`}
                    >
                      <td className="px-2 py-2 align-top">
                        <input
                          type="text"
                          value={row.widthInput}
                          onChange={(event) => handleDimensionChange(index, 'width', event.target.value)}
                          onBlur={(event) => handleDimensionBlur(index, 'width', event.target.value)}
                          className="w-24 rounded border border-brand-tan/40 bg-white px-2 py-1 text-sm dark:border-amber-700/40 dark:bg-gray-900"
                          inputMode="decimal"
                          aria-label={t('pricing.table.width_input_label', 'Width for {label}', { label: row.label })}
                        />
                      </td>
                      <td className="px-2 py-2 align-top">
                        <input
                          type="text"
                          value={row.heightInput}
                          onChange={(event) => handleDimensionChange(index, 'height', event.target.value)}
                          onBlur={(event) => handleDimensionBlur(index, 'height', event.target.value)}
                          className="w-24 rounded border border-brand-tan/40 bg-white px-2 py-1 text-sm dark:border-amber-700/40 dark:bg-gray-900"
                          inputMode="decimal"
                          aria-label={t('pricing.table.height_input_label', 'Height for {label}', { label: row.label })}
                        />
                      </td>
                      <td className="px-2 py-2 align-top">
                        <select
                          value={(baseRow?.discount ?? DEFAULT_DISCOUNT).toString()}
                          onChange={(event) => handleDiscountChange(index, event.target.value)}
                          className="w-24 rounded border border-brand-tan/40 bg-white px-2 py-1 text-sm dark:border-amber-700/40 dark:bg-gray-900"
                          aria-label={t('pricing.table.discount_input_label', 'Discount for row {index}', { index: index + 1 })}
                        >
                          {DISCOUNT_SUGGESTIONS.map((value) => (
                            <option key={value} value={value.toString()}>
                              {`${value}%`}
                            </option>
                          ))}
                        </select>
                      </td>
                      {sortedPaperTypes.map((paper: PricingSummary['paperTypes'][number]) => {
                        const price = pricingResult?.paperTypePrices[paper.id]?.[index] ?? null;
                        return (
                          <td key={`${row.label}-${paper.id}`} className="px-3 py-2">
                            {isSupportedSize ? formatPrice(price) : '—'}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mt-6 p-4 bg-brand-tan/10 dark:bg-amber-900/20 rounded-lg border border-brand-tan/30 dark:border-amber-700/30">
          <h3 className="font-semibold text-lg mb-2 text-brand-brown dark:text-gray-100">
            {t('pricing.note_title', 'Important Notes')}
          </h3>
          <ul className="list-disc pl-6 space-y-1 text-sm">
            <li>
              {t(
                'pricing.note_1',
                'All prices are in USD and include premium quality printing with Epson UltraChrome Pro12 pigment inks.'
              )}
            </li>
            <li>
              {t(
                'pricing.note_2',
                'The sizes above are examples. You can enter any custom size in the table above to see the price; the same custom sizes are available during ordering.'
              )}
            </li>
            <li>
              {t(
                'pricing.note_customization',
                'You can experiment with your own dimensions and discount selections in the table above.'
              )}
            </li>
            <li>
              {t(
                'pricing.note_3',
                'Delivery costs are calculated separately based on your location and shipping method.'
              )}
            </li>
            <li>
              {t(
                'pricing.note_4',
                'Each print undergoes manual quality inspection before shipping.'
              )}
            </li>
            <li>
              {t(
                'pricing.note_delivery_worldwide',
                'We deliver printed arts worldwide. You can see the actual shipping cost during ordering.'
              )}
            </li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-2xl font-semibold text-brand-brown dark:text-gray-100">
            {t('pricing.paper_title', 'About Our Papers')}
          </h2>
          <div className="space-y-4">
            {sortedPaperTypes.map((paper: PricingSummary['paperTypes'][number]) => (
              <div
                key={`desc-${paper.id}`}
                id={`paper-${paper.id}`}
                className="border border-brand-tan/20 dark:border-amber-700/30 rounded-lg p-4 bg-white/70 dark:bg-gray-800/60"
              >
                <h3 className="font-semibold text-lg text-brand-green dark:text-gray-100">
                  {paper.name}
                </h3>
                <div
                  className="mt-1 text-sm text-brand-brown/80 dark:text-gray-300"
                  dangerouslySetInnerHTML={{
                    __html: paper.description
                      ? DOMPurify.sanitize(paper.description, { USE_PROFILES: { html: true } })
                      : t('pricing.paper.description_missing', 'Description coming soon.'),
                  }}
                />
                <a
                  href={`#${PRICING_TABLE_TOP_ANCHOR}`}
                  className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-brand-green hover:underline focus-visible:underline focus-visible:outline-none dark:text-amber-300"
                >
                  ↑ {t('pricing.back_to_top', 'Back to table')}
                </a>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default PricingPage;
